import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import api from "@/lib/axios"
import { DataLoaPasca, Option } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { MdModeEdit } from "react-icons/md"
import { useMutation, useQueryClient } from "@tanstack/react-query"

const patchData = async (newData: DataLoaPasca) => {
  const response = await api.patch("/operator-pasca/data-loa/update", newData)
  return response.data
}

export default function Form({
  selectedRow,
  closeModal,
}: {
  selectedRow?: DataLoaPasca | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<DataLoaPasca>({
    id_data_loa_pasca: selectedRow?.id_data_loa_pasca || "",
    id_data_pendaftaran: selectedRow?.id_data_pendaftaran || "",
    catatan_peserta: "",
    catatan_operator: selectedRow?.catatan_operator || "",
    status: selectedRow?.status || "",
    nomor_loa: selectedRow?.nomor_loa || "",
    tanggal_loa: selectedRow?.tanggal_loa || "",
    file_loa: "",
  })

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: patchData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["listDataLoa"],
      })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={<MdModeEdit />}
        title={"Aprroval LOA"}
        description={`${selectedRow?.data_pendaftaran?.data_siswa?.nama_lengkap} | ${selectedRow?.data_pendaftaran?.nomor_pendaftaran}`}
      />
      <div>
        <Label>Status Persetujuan</Label>
        <Select
          required
          value={String(form.status)}
          onValueChange={(value) => {
            setForm((prev) => ({ ...prev, status: value }))
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={"Pilih Status"} />
          </SelectTrigger>
          <SelectContent>
            {[
              { label: "Setuju", value: "1" },
              { label: "Tolak", value: "2" },
            ]?.map((option: Option) => (
              <SelectItem key={option.value} value={`${option.value}`}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {form.status == "1" ? (
        <>
          <div>
            <Label>Nomor LOA</Label>
            <Input
              placeholder="Ketik nomor loa..."
              value={form.nomor_loa}
              onChange={(event) => {
                setForm((prev) => ({
                  ...prev,
                  nomor_loa: event.target.value,
                }))
              }}
            />
          </div>
          <div>
            <Label>Tanggal LOA</Label>
            <Input
              type="date"
              value={form.tanggal_loa}
              onChange={(event) => {
                setForm((prev) => ({
                  ...prev,
                  tanggal_loa: event.target.value,
                }))
              }}
            />
          </div>
        </>
      ) : (
        ""
      )}

      {form.status == "2" ? (
        <>
          <Label>Catatan</Label>
          <Textarea
            placeholder="Ketik catatan..."
            value={form.catatan_operator}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                catatan_operator: event.target.value,
              }))
            }
          />
        </>
      ) : (
        ""
      )}

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
